import { ReactNode, useCallback } from 'react';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { Fraction } from '@ui/components/Fraction';
import { PhoenixRoundLogo } from '@ui/components/logos/PhoenixRoundLogo/PhoenixRoundLogo';

import type { CellResult, GameParticipantsResult, GameSide } from './types';

import styles from './GameResultsBadge.module.css';

const DEFAULT_SKIPPED_ICON = <PhoenixRoundLogo preset="custom" />;

export type Props = {
  className?: string;
  gameId?: string | null;
  gameLink?: string;
  additional?: ReactNode;
  large?: boolean;
  fullWidth?: boolean;
  // @deprecated используейте cellResult (если возможно)
  result?: GameParticipantsResult;
  cellResult?: CellResult | null;
  side?: GameSide;
  score?: number | null;
  dotCentered?: boolean;
  reversed?: boolean;
  skippedIcon?: ReactNode;
  onGameClick?: (gameId: string) => void;
  onMouseEnter?: (gameId: string, el: HTMLElement) => void;
  onMouseLeave?: () => void;
};
export function GameResultsBadge({
  className,
  gameId,
  gameLink,
  additional,
  large,
  fullWidth,
  result,
  cellResult,
  side,
  score,
  reversed,
  dotCentered,
  skippedIcon = DEFAULT_SKIPPED_ICON,
  onGameClick,
  onMouseEnter,
  onMouseLeave,
}: Props) {
  const isSkipped = cellResult === 'NoGamePossible' || /* deprecate */ result === 'Skipped';
  const isUnPlayed =
    cellResult === 'NotStarted' ||
    cellResult === 'NotDrafted' ||
    /* deprecate */
    result === 'UnPlayed';
  const isInProgress = cellResult === 'InProgress' || /* deprecate */ result === 'InProgress';
  const forfeitIndicator =
    cellResult === 'ForfeitWin'
      ? '+'
      : cellResult === 'ForfeitLoss' || cellResult === 'BanForfeitLoss'
        ? '-'
        : null;

  const hasLink = gameLink && gameId;
  const hasClickHandler = onGameClick && gameId;

  const onClick = useCallback(() => {
    if (hasClickHandler) onGameClick(gameId);
  }, [onGameClick, gameId, hasClickHandler]);

  const handleMouseEnter = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType === 'touch') return;

      if (onMouseEnter && gameId) {
        onMouseEnter(gameId, e.currentTarget);
      }
    },
    [onMouseEnter, gameId],
  );

  const handleMouseLeave = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType === 'touch') return;

      onMouseLeave?.();
    },
    [onMouseLeave],
  );

  const content = (
    <>
      <span>
        {isSkipped ? (
          <div className={styles.icon}>{skippedIcon}</div>
        ) : isInProgress ? (
          <span>C</span>
        ) : isUnPlayed && !score ? null : (
          (forfeitIndicator ?? <Fraction value={score?.toString()} />)
        )}
      </span>

      {!isSkipped && (
        <div className={styles.rightColumn}>
          <span className={styles.additionalNumber}>{additional}</span>

          {!isUnPlayed && (
            <div
              className={cx(styles.sideDot, side && styles[side], {
                [styles.centered]: dotCentered,
              })}
            />
          )}
        </div>
      )}
    </>
  );

  const classes = cx(
    styles.gameResultsBadge,
    result && styles[result],
    cellResult && styles[cellResult],
    className,
    {
      [styles.reversed]: reversed,
      [styles.clickable]: hasLink || hasClickHandler,
      [styles.large]: large,
      [styles.fullWidth]: fullWidth,
    },
  );

  return hasLink ? (
    <Link
      to={gameLink}
      className={classes}
      onPointerEnter={handleMouseEnter}
      onPointerLeave={handleMouseLeave}
    >
      {content}
    </Link>
  ) : (
    <div
      className={classes}
      onClick={hasClickHandler ? onClick : undefined}
      onPointerEnter={handleMouseEnter}
      onPointerLeave={handleMouseLeave}
    >
      {content}
    </div>
  );
}
